<template>
  <Row gutters="16">
    <Col span="20">
      <Row gutters="16" class="mb-2">
        <Col span="4">
          <strong>{{ $l("components.settings.timezone", "Timezone") }}</strong>
        </Col>

        <Col span="14">
          <Select
            v-model="form.timezone"
            :placeholder="$l('components.settings.timezone', 'Timezone')"
            :not-found-text="$l('components.settings.empty', 'N/A')"
            filterable
          >
            <Option v-for="item in timezonesList" :value="item" :key="item">{{
              item
            }}</Option>
          </Select>
        </Col>
      </Row>

      <Row gutters="16" class="mb-2">
        <Col span="24">
          <strong>{{ $l("components.settings.database", "Database") }}</strong>
        </Col>

        <!-- Repeat -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.repeat", "Repeat") }}</span>
          </Col>

          <Col span="14">
            <Select
              v-model="form.database.repeat"
              :placeholder="$l('components.settings.repeat', 'Repeat')"
            >
              <Option v-for="item in repeatsList" :value="item" :key="item">{{
                $l(`components.settings.${item}`, item)
              }}</Option>
            </Select>
          </Col>
        </Row>

        <!-- Schedule -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.schedule", "Schedule") }}</span>
          </Col>

          <Col span="14">
            <Row :gutter="16">
              <Col span="12">
                <Select
                  v-model="form.database.schedule.interval"
                  :placeholder="$l('components.settings.interval', 'Interval')"
                  :disabled="form.database.repeat !== 'beats'"
                >
                  <Option
                    v-for="item in form.database.schedule.unit === 'hours'
                      ? intervalHoursList
                      : intervalMinutesList"
                    :value="item"
                    :key="item"
                    >{{ item }}</Option
                  >
                </Select>
              </Col>
              <Col span="12">
                <Select
                  v-model="form.database.schedule.unit"
                  :disabled="form.database.repeat !== 'beats'"
                  :placeholder="$l('components.settings.unit', 'Unit')"
                >
                  <Option
                    v-for="item in ['minutes', 'hours']"
                    :value="item"
                    :key="item"
                    >{{ item }}</Option
                  >
                </Select>
              </Col>
            </Row>
          </Col>
        </Row>

        <!-- Start on -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.startOn", "Start on") }}</span>
          </Col>

          <Col span="14">
            <TimePicker
              v-model="form.database.startOn"
              format="HH:mm"
              :disabled="
                form.database.repeat !== 'daily' &&
                form.database.repeat !== 'weekly'
              "
              :clearable="false"
              :placeholder="$l('components.settings.startOn', 'Start on')"
              style="width: 100%"
            />
          </Col>
        </Row>

        <!-- Repeat on -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.repeatOn", "Repeat on") }}</span>
          </Col>

          <Col
            span="2"
            class="day-picker"
            :class="{
              selected: checkSelectedDay('database', item.value),
              disabled: form.database.repeat !== 'weekly',
            }"
            v-for="(item, i) in daysList"
            :key="i"
            ><div
              style="width: 100%"
              @click="selectDay(item.value, 'database')"
            >
              {{ item.label }}
            </div></Col
          >
        </Row>
      </Row>

      <!-- Engine -->

      <Row gutters="16" class="mb-2">
        <Col span="24">
          <strong>{{ $l("components.settings.engine", "Engine") }}</strong>
        </Col>

        <!-- Repeat -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.repeat", "Repeat") }}</span>
          </Col>

          <Col span="14">
            <Select
              v-model="form.engine.repeat"
              :placeholder="$l('components.settings.repeat', 'Repeat')"
            >
              <Option v-for="item in repeatsList" :value="item" :key="item">{{
                $l(`components.settings.${item}`, item)
              }}</Option>
            </Select>
          </Col>
        </Row>

        <!-- Schedule -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.schedule", "Schedule") }}</span>
          </Col>

          <Col span="14">
            <Row :gutter="16">
              <Col span="12">
                <Select
                  v-model="form.engine.schedule.interval"
                  :disabled="form.engine.repeat !== 'beats'"
                  :placeholder="$l('components.settings.interval', 'Interval')"
                >
                  <Option
                    v-for="item in form.engine.schedule.unit === 'hours'
                      ? intervalHoursList
                      : intervalMinutesList"
                    :value="item"
                    :key="item"
                    >{{ item }}</Option
                  >
                </Select>
              </Col>
              <Col span="12">
                <Select
                  v-model="form.engine.schedule.unit"
                  :placeholder="$l('components.settings.unit', 'Unit')"
                  :disabled="form.engine.repeat !== 'beats'"
                >
                  <Option
                    v-for="item in ['minutes', 'hours']"
                    :value="item"
                    :key="item"
                    >{{ item }}</Option
                  >
                </Select>
              </Col>
            </Row>
          </Col>
        </Row>

        <!-- Start on -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.startOn", "Start on") }}</span>
          </Col>

          <Col span="14">
            <TimePicker
              v-model="form.engine.startOn"
              format="HH:mm"
              :disabled="
                form.engine.repeat !== 'daily' &&
                form.engine.repeat !== 'weekly'
              "
              :clearable="false"
              :placeholder="$l('components.settings.startOn', 'Start on')"
              style="width: 100%"
            />
          </Col>
        </Row>

        <!-- Repeat on -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.repeatOn", "Repeat on") }}</span>
          </Col>

          <Col
            span="2"
            class="day-picker"
            :class="{
              selected: checkSelectedDay('engine', item.value),
              disabled: form.engine.repeat !== 'weekly',
            }"
            v-for="(item, i) in daysList"
            :key="i"
            ><div style="width: 100%" @click="selectDay(item.value, 'engine')">
              {{ item.label }}
            </div></Col
          >
        </Row>
      </Row>

      <!-- Installer -->

      <Row gutters="16" class="mb-2">
        <Col span="24">
          <strong>{{
            $l("components.settings.installer", "Installer")
          }}</strong>
        </Col>

        <!-- Repeat -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.repeat", "Repeat") }}</span>
          </Col>

          <Col span="14">
            <Select
              v-model="form.installer.repeat"
              :placeholder="$l('components.settings.repeat', 'Repeat')"
            >
              <Option v-for="item in repeatsList" :value="item" :key="item">{{
                $l(`components.settings.${item}`, item)
              }}</Option>
            </Select>
          </Col>
        </Row>

        <!-- Schedule -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.schedule", "Schedule") }}</span>
          </Col>

          <Col span="14">
            <Row :gutter="16">
              <Col span="12">
                <Select
                  v-model="form.installer.schedule.interval"
                  :disabled="form.installer.repeat !== 'beats'"
                  :placeholder="$l('components.settings.interval', 'Interval')"
                >
                  <Option
                    v-for="item in form.installer.schedule.unit === 'hours'
                      ? intervalHoursList
                      : intervalMinutesList"
                    :value="item"
                    :key="item"
                    >{{ item }}</Option
                  >
                </Select>
              </Col>
              <Col span="12">
                <Select
                  v-model="form.installer.schedule.unit"
                  :placeholder="$l('components.settings.unit', 'Unit')"
                  :disabled="form.installer.repeat !== 'beats'"
                >
                  <Option
                    v-for="item in ['minutes', 'hours']"
                    :value="item"
                    :key="item"
                    >{{ item }}</Option
                  >
                </Select>
              </Col>
            </Row>
          </Col>
        </Row>

        <!-- Start on -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.startOn", "Start on") }}</span>
          </Col>

          <Col span="14">
            <TimePicker
              v-model="form.installer.startOn"
              format="HH:mm"
              :disabled="
                form.installer.repeat !== 'daily' &&
                form.installer.repeat !== 'weekly'
              "
              :clearable="false"
              :placeholder="$l('components.settings.startOn', 'Start on')"
              style="width: 100%"
            />
          </Col>
        </Row>

        <!-- Repeat on -->
        <Row gutters="16" class="mb-2">
          <Col span="4">
            <span>{{ $l("components.settings.repeatOn", "Repeat on") }}</span>
          </Col>

          <Col
            span="2"
            class="day-picker"
            :class="{
              selected: checkSelectedDay('installer', item.value),
              disabled: form.installer.repeat !== 'weekly',
            }"
            v-for="(item, i) in daysList"
            :key="i"
            ><div
              style="width: 100%"
              @click="selectDay(item.value, 'installer')"
            >
              {{ item.label }}
            </div></Col
          >
        </Row>
      </Row>

      <Row gutters="16">
        <Col span="18">
          <div class="field is-grouped is-grouped-right">
            <div class="control">
              <Button
                :loading="loading"
                @click="update"
                type="primary"
                :disabled="!checkCanClickUpdate"
              >
                {{ $l("global.update", "Update") }}</Button
              >
            </div>
          </div>
        </Col>
      </Row>
    </Col>
  </Row>
</template>

<script>
import SettingsApi from "@/api/Settings";
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Checkbox,
  Button,
  Form,
  FormItem,
  Input,
  Icon,
  Row,
  Col,
  Select,
  Option,
  TimePicker,
} from "iview";

import moment from "moment-timezone";

export default {
  components: {
    TimePicker,
    Dropdown,
    DropdownMenu,
    DropdownItem,
    Checkbox,
    Button,
    Form,
    FormItem,
    Input,
    Icon,
    Row,
    Col,
    Select,
    Option,
  },

  data() {
    return {
      loading: false,

      form: {
        timezone: "Asia/Ho_Chi_Minh",
        database: {
          repeat: "beats",
          schedule: {
            interval: 20,
            unit: "hours",
          },
          startOn: "00:00",
          repeatOn: [],
        },

        engine: {
          repeat: "beats",
          schedule: {
            interval: 20,
            unit: "hours",
          },
          startOn: "00:00",
          repeatOn: [],
        },

        installer: {
          repeat: "beats",
          schedule: {
            interval: 20,
            unit: "hours",
          },
          startOn: "00:00",
          repeatOn: [],
        },
      },

      timezonesList: [],

      repeatsList: ["beats", "daily", "weekly", "none"],

      intervalHoursList: Array.from({ length: 24 }, (_, index) => index + 1),
      intervalMinutesList: Array.from({ length: 176 }, (_, index) => index + 5),

      daysList: [
        {
          label: "SUN",
          value: "0",
        },
        {
          label: "MON",
          value: "1",
        },
        {
          label: "TUE",
          value: "2",
        },
        {
          label: "WED",
          value: "3",
        },
        {
          label: "THU",
          value: "4",
        },
        {
          label: "FRI",
          value: "5",
        },
        {
          label: "SAT",
          value: "6",
        },
      ],
    };
  },
  computed: {
    checkCanClickUpdate() {
      if (!this.form.timezone) {
        return false;
      }
      // Database
      if (this.form.database.repeat === "beats") {
        if (!this.form.database.schedule.interval) {
          return false;
        }
      } else {
        if (this.form.database.repeat === "weekly") {
          if (this.form.database.repeatOn.length === 0) {
            return false;
          }
        }
      }

      // Engine

      if (this.form.engine.repeat === "beats") {
        if (!this.form.engine.schedule.interval) {
          return false;
        }
      } else {
        if (this.form.engine.repeat === "weekly") {
          if (this.form.engine.repeatOn.length === 0) {
            return false;
          }
        }
      }

      // Installer

      if (this.form.installer.repeat === "beats") {
        if (!this.form.installer.schedule.interval) {
          return false;
        }
      } else {
        if (this.form.installer.repeat === "weekly") {
          if (this.form.installer.repeatOn.length === 0) {
            return false;
          }
        }
      }

      return true;
    },
  },

  methods: {
    update() {
      let sent = {
        timezone: "",
        database: {},
        engine: {},
        installer: {},
      };

      sent.timezone = this.form.timezone;
      // Database

      if (this.form.database.repeat === "beats") {
        sent.database.repeat = "beats";
        sent.database.unit = this.form.database.schedule.unit;
        sent.database.interval = this.form.database.schedule.interval;
      } else {
        if (this.form.database.repeat === "daily") {
          sent.database.repeat = "daily";
          sent.database.time = this.form.database.startOn;
        } else {
          if (this.form.database.repeat === "weekly") {
            sent.database.repeat = "weekly";
            sent.database.time = this.form.database.startOn;
            sent.database.date = this.form.database.repeatOn;
          } else {
            if (this.form.database.repeat === "none") {
              sent.database.repeat = "none";
            }
          }
        }
      }

      // Engine

      if (this.form.engine.repeat === "beats") {
        sent.engine.repeat = "beats";
        sent.engine.unit = this.form.engine.schedule.unit;
        sent.engine.interval = this.form.engine.schedule.interval;
      } else {
        if (this.form.engine.repeat === "daily") {
          sent.engine.repeat = "daily";
          sent.engine.time = this.form.engine.startOn;
        } else {
          if (this.form.engine.repeat === "weekly") {
            sent.engine.repeat = "weekly";
            sent.engine.time = this.form.engine.startOn;
            sent.engine.date = this.form.engine.repeatOn;
          } else {
            if (this.form.engine.repeat === "none") {
              sent.engine.repeat = "none";
            }
          }
        }
      }

      // Installer

      if (this.form.installer.repeat === "beats") {
        sent.installer.repeat = "beats";
        sent.installer.unit = this.form.installer.schedule.unit;
        sent.installer.interval = this.form.installer.schedule.interval;
      } else {
        if (this.form.installer.repeat === "daily") {
          sent.installer.repeat = "daily";
          sent.installer.time = this.form.installer.startOn;
        } else {
          if (this.form.installer.repeat === "weekly") {
            sent.installer.repeat = "weekly";
            sent.installer.time = this.form.installer.startOn;
            sent.installer.date = this.form.installer.repeatOn;
          } else {
            if (this.form.installer.repeat === "none") {
              sent.installer.repeat = "none";
            }
          }
        }
      }

      this.loading = true;

      SettingsApi.scheduleUpdate(this.$http, sent)
        .then((res) => {
          this.$store.dispatch("NOTIFY", {
            type: "success",
            text: res.message,
            duration: 10000,
          });
          this.loading = false;

          this.getScheduleUpdate();
        })

        .catch(() => {
          this.loading = false;
        });
    },

    checkSelectedDay(type, value) {
      const x = this.form[type].repeatOn.find((item) => item === value);

      if (!x) {
        return false;
      }

      return true;
    },

    selectDay(value, type) {
      if (this.checkSelectedDay(type, value)) {
        this.form[type].repeatOn = this.form[type].repeatOn.filter(
          (item) => item !== value
        );
      } else {
        this.form[type].repeatOn.push(value);
      }
    },

    // Get Schedule Update

    getScheduleUpdate() {
      this.loading = true;
      SettingsApi.getScheduleUpdate(this.$http)
        .then((res) => {
          this.loading = false;

          this.form = {
            timezone: res.timezone || "Asia/Ho_Chi_Minh",
            database: {
              repeat: res.database.repeat || "beats",
              startOn: res.database.time || "00:00",
              schedule: {
                interval: res.database.interval || 0,
                unit: res.database.unit || "hours",
              },
              repeatOn:
                res.database.date && res.database.date.length > 0
                  ? res.database.date
                  : [],
            },

            engine: {
              repeat: res.engine.repeat || "beats",
              startOn: res.engine.time || "00:00",
              schedule: {
                interval: res.engine.interval || 0,
                unit: res.engine.unit || "hours",
              },
              repeatOn:
                res.engine.date && res.engine.date.length > 0
                  ? res.engine.date
                  : [],
            },

            installer: {
              repeat: res.installer.repeat || "beats",
              startOn: res.installer.time || "00:00",
              schedule: {
                interval: res.installer.interval || 0,
                unit: res.installer.unit || "hours",
              },
              repeatOn:
                res.installer.date && res.installer.date.length > 0
                  ? res.installer.date
                  : [],
            },
          };
        })

        .catch(() => {
          this.loading = false;
        });
    },
  },

  created() {
    this.timezonesList = moment.tz.names();

    this.getScheduleUpdate();
  },
};
</script>

<style lang="scss" scoped>
.day-picker {
  height: 30px;
  border-radius: 3px;
  border: 1px solid #c7cdcf;
  text-align: center;
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.selected {
    border: 2px solid #3081ed;
    color: #3081ed;
  }
}
</style>
